import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';

import useAuth from '../../auth/useAuth';
import AuthService from '../../auth/AuthService';
import { useTour } from '../../context/TourContext';
import { updateUserTournamentOnboarding } from '../Login/api';

import {
  tournamentsQuery,
  tournamentsRoundsQuery,
  tournamentsMatchupsQuery,
  tournamentsTeamsQuery,
  tournamentsTeamScoresQuery,
  tournamentsUserScoresQuery,
  gameQuery,
  sessionsQuery,
  createGameSession
} from './api';

import Layout from '../UI/introGameLayout/layout';

import ReviewSessionHeader from '../UI/reviewSession/header';
import ReviewSessionTournamentBracket from '../UI/reviewSession/tournamentBracket';
import Alert from '../UI/alert';
import {
  CelebrationModal,
  LeaderCelebration,
  WinnersLosers,
  Champions,
  SecondPlace
} from '../UI/reviewSession/celebrations';
import { roundNames } from '../UI/reviewSession/data';

// Per-game-type tutorial / level URLs for direct navigation. Bypassing
// /acc/transition for tournament play removes the createGameSession race
// that otherwise sends students into a tutorial loop.
const TUTORIAL_URLS = {
  'Account Classification': '/classification/level/1/tutorial',
  'Normal Balance':         '/normal/level/1/tutorial',
  'Balanced Equation':      '/balanced-equation/level/1/tutorial',
  'The Principles':         '/the-principles/level/1/tutorial',
};
const LEVEL_URLS = {
  'Account Classification': '/classification/level/1',
  'Normal Balance':         '/normal/level/1',
  'Balanced Equation':      '/balanced-equation/level/1',
  'The Principles':         '/the-principles/level/1',
};

export default function ReviewSession() {
  const params = useParams();
  const tournamentId = Number(params.tournamentId);

  const { saveCurrentGameId, getUser } = useAuth();
  const { startTour, isActive: isTourActive } = useTour();
  const tourStartedRef = useRef(false);
  const user = getUser();

  const [tournament, setTournament] = useState(null);
  const [currentRound, setCurrentRound] = useState(1);
  const [userScores, setUserScores] = useState(0);
  const [isStartButtonLoading, setIsStartButtonLoading] = useState(false);
  const [isUpdateScoresButtonLoading, setIsUpdateScoresButtonLoading] = useState(false);
  const [isScoresUpdated, setIsScoresUpdated] = useState(false);
  
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState('error');
  const [alertTitle, setAlertTitle] = useState('Error');
  const [alertText, setAlertText] = useState('');
  const [celebration, setCelebration] = useState(null);
  const prevUserTeamIdRef = useRef(null);

  const { data: tournaments } = useQuery(tournamentsQuery());
  const { data: tournamentsRounds, refetch: refetchTournamentsRounds } = useQuery(tournamentsRoundsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsMatchups } = useQuery(tournamentsMatchupsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsTeams } = useQuery(tournamentsTeamsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsTeamScores, refetch: refetchTournamentsTeamsScores } = useQuery(tournamentsTeamScoresQuery({tournamentId: tournament?.id}));
  const { data: usersScores, refetch: refetchTournamentsUserScores } = useQuery(tournamentsUserScoresQuery({tournamentId: tournament?.id}));

  const queryClient = useQueryClient();
  
  const refetchSessions = async ({gameId}) => {
    const data = await queryClient.fetchQuery({
      ...sessionsQuery({ gameId })
    });
    return data;
  };
  
  // Only fetch the games we actually consume — the first round (its
  // gameType seeds the student-tournament-overview tour copy) and the
  // current round (whose game data drives the start-exercise CTA and
  // session creation). Others are pre-fetched purely as dead weight.
  const roundGamesQueries = useQueries({
    queries:
      tournamentsRounds?.map((round, i) => ({
        ...gameQuery({ id: round.game_id }),
        enabled: !!round.game_id && (i === 0 || i === currentRound - 1),
      })) || [],
  });

  const tournamentRoundGames = tournamentsRounds?.map((round, i) => {
    const gameData = roundGamesQueries[i]?.data;
    return {
      ...round,
      gameType: gameData?.game_type || null,
      game: gameData || null,
    };
  }) || [];

  useEffect(() => {
    if(tournaments && tournamentId) {
      const currentTournament = tournaments.find(tournament => tournament.id === tournamentId);

      setTournament(currentTournament);
    }
  }, [tournaments, tournamentId]);

  useEffect(() => {
    if(usersScores?.length) {
      const currentUserScores = usersScores.filter(score => score.user_id === user.id);
      const currentRoundUserScores = currentUserScores.reduce((sum, score) => sum + score.user_total_points, 0);

      setUserScores(currentRoundUserScores);
    }
  }, [usersScores, user.id]);

  useEffect(() => {
    if (!tournamentsRounds?.length) return;

    const activeRound = tournamentsRounds.find(round => round.is_active);
    const unfinishedRound = tournamentsRounds.find(round => !round.is_finished);
    const lastRound = tournamentsRounds[tournamentsRounds.length - 1];

    const roundToSet = activeRound || unfinishedRound || lastRound;

    setCurrentRound(prev => {
      if (prev !== roundToSet.number) {
        setIsScoresUpdated(false);
        return roundToSet.number;
      }
      return prev;
    });
  }, [tournamentsRounds]);

  // Detect when the student has been moved onto a new team (i.e. their
  // previous team lost and they were absorbed into a winning team). Fires
  // the Winners & Losers celebration once per transition, but only when
  // both sides are clean WINNER/LOSER — tied teams are excluded from the
  // popup per celebration rules.
  useEffect(() => {
    const userTeam = tournamentsTeams?.find(team => team.user_ids.includes(user.id));
    if (!userTeam) return;

    if (prevUserTeamIdRef.current == null) {
      prevUserTeamIdRef.current = userTeam.id;
      return;
    }

    if (prevUserTeamIdRef.current !== userTeam.id) {
      const priorTeamId = prevUserTeamIdRef.current;
      const priorTeam = tournamentsTeams.find(t => t.id === priorTeamId);
      prevUserTeamIdRef.current = userTeam.id;

      const priorRoundNumber = currentRound - 1;
      const priorTeamScore = tournamentsTeamScores?.find(
        s => s.team_id === priorTeamId && s.round_number === priorRoundNumber
      );
      const userTeamScore = tournamentsTeamScores?.find(
        s => s.team_id === userTeam.id && s.round_number === priorRoundNumber
      );

      const winnerTeams = userTeamScore?.team_result === 'WINNER' ? [userTeam] : [];
      const loserTeams  = priorTeam && priorTeamScore?.team_result === 'LOSER' ? [priorTeam] : [];

      if (!winnerTeams.length && !loserTeams.length) return;

      const roundCount = tournament?.round_count || tournamentsRounds?.length || 0;
      const roundName = roundNames[roundNames.length - roundCount + priorRoundNumber] || '';
      setCelebration({
        type: 'winnersLosers',
        winnerTeams,
        loserTeams,
        roundName
      });
    }
  }, [tournamentsTeams, tournamentsTeamScores, user.id, tournament?.round_count, tournamentsRounds?.length, currentRound]);

  // Trigger the student-tournament-overview tour once for users who
  // haven't seen it yet. Mirrors the instructor flow but uses a different
  // step set tailored to the student perspective.
  useEffect(() => {
    if (tourStartedRef.current) return;
    if (isTourActive) return;
    if (!tournament || !tournamentsRounds?.length || !tournamentsTeams?.length) return;

    const firstGameType = tournamentRoundGames[0]?.gameType;
    if (!firstGameType) return;

    const cachedUser = AuthService.getUser();
    if (!cachedUser?.id) return;
    if (cachedUser.onboarded_tournament === true) return;

    tourStartedRef.current = true;
    startTour('student-tournament-overview', {
      data: {
        tournament,
        firstGameType,
      },
      onComplete: async () => {
        const current = AuthService.getUser();
        if (current?.onboarded_tournament === true) return;
        try {
          await updateUserTournamentOnboarding({ id: cachedUser.id });
          AuthService.setUser({ ...current, onboarded_tournament: true });
        } catch (err) {
          console.error('Failed to mark tournament onboarding complete:', err);
        }
      },
    });
  }, [tournament, tournamentsRounds, tournamentsTeams, tournamentRoundGames, isTourActive, startTour]);

  // Synchronous guard — second click fires before React has flipped isStartButtonLoading, so the ref short-circuits those re-entries.
  const isStartInFlightRef = useRef(false);

  const handleStartButtonClick = async () => {
    if (isStartInFlightRef.current) return;
    isStartInFlightRef.current = true;
    setIsStartButtonLoading(true);
    try {
      const currentRoundGame = tournamentRoundGames[currentRound - 1];
      const currentGameId = currentRoundGame.game_id;
      const currentGameType = currentRoundGame.gameType;

      const gameSessions = await refetchSessions({ gameId: currentGameId });

      if(gameSessions?.length && gameSessions[0].completed === true) {
        setAlertType('info');
        setAlertTitle('All Done');
        setAlertText('You\'ve completed the exercise');
        setIsAlertVisible(true);
        setIsStartButtonLoading(false);
        return;
      }

      const userSession = gameSessions?.find(s => s.user === user.id && s.game === currentGameId);
      const isFirstTime = !userSession;

      // Awaited POST creates the session with is_ready: true so the level
      // page's bounce check passes once the new tab loads.
      if (isFirstTime) {
        try {
          await createGameSession({ game: currentGameId, user: user.id });
        } catch (err) {
          console.error('Failed to create tournament game session', err);
          // BE returns 400 with { game: ["You are not a member of this game's course."] } when the student isn't enrolled — surface the actual reason instead of a generic "try again".
          const notAMember = err?.response?.status === 400 && /not a member/i.test(JSON.stringify(err?.response?.data || ''));
          setAlertType('error');
          setAlertTitle(notAMember ? 'No Access' : 'Error');
          setAlertText(notAMember
            ? 'You don\'t have access to this exercise. Please contact your instructor.'
            : 'Could not start the exercise. Please try again.');
          setIsAlertVisible(true);
          setIsStartButtonLoading(false);
          return;
        }
      }

      saveCurrentGameId(currentGameId, currentGameType);
      setIsStartButtonLoading(false);

      // Skip /acc/transition entirely for tournaments. First-time players
      // see the tutorial; returning players go straight to the level.
      const fallbackUrl = `/${(currentGameType || '').toLowerCase().replace(/\s+/g, '-')}/level/1${isFirstTime ? '/tutorial' : ''}`;
      const targetUrl = isFirstTime
        ? (TUTORIAL_URLS[currentGameType] || fallbackUrl)
        : (LEVEL_URLS[currentGameType]    || fallbackUrl);

      window.open(targetUrl, '_blank');
    } finally {
      isStartInFlightRef.current = false;
    }
  }

  const handleUpdateScoresButtonClick = async () => {
    setIsUpdateScoresButtonLoading(true);

    const { data: updatedTournamentsScores } = await refetchTournamentsTeamsScores();
    const currentRoundUpdatedTournamentScores = updatedTournamentsScores.filter(score => score.round_number === currentRound).reduce((sum, score) => sum + score.team_total_points, 0);

    if(currentRoundUpdatedTournamentScores > 0) {
      const { data: usersScores } = await refetchTournamentsUserScores();
      const currentUserScores = usersScores.filter(score => score.user_id === user.id);
      const currentRoundUserScores = currentUserScores.reduce((sum, score) => sum + score.user_total_points, 0);
      setUserScores(currentRoundUserScores);
      setIsScoresUpdated(true);

      const userTeam = tournamentsTeams?.find(team => team.user_ids.includes(user.id));
      const roundCount = tournament?.round_count || tournamentsRounds?.length || 0;
      const isChampionshipRound = currentRound === roundCount;
      const currentRoundData = tournamentsRounds?.[currentRound - 1];

      if (userTeam) {
        const userTeamScore = updatedTournamentsScores.find(
          s => s.round_number === currentRound && s.team_id === userTeam.id
        );

        if (isChampionshipRound && currentRoundData?.is_finished) {
          const championshipMatchup = tournamentsMatchups?.find(m => m.round_id === currentRoundData.id);
          if (championshipMatchup) {
            const matchupScores = updatedTournamentsScores.filter(s => s.matchup_id === championshipMatchup.id);
            const winnerScoreRow = matchupScores.find(s => s.team_result === 'WINNER');
            const loserScoreRow  = matchupScores.find(s => s.team_result === 'LOSER');
            const winnerTeam = winnerScoreRow ? tournamentsTeams.find(t => t.id === winnerScoreRow.team_id) : null;
            const loserTeam  = loserScoreRow  ? tournamentsTeams.find(t => t.id === loserScoreRow.team_id)  : null;
            if (winnerTeam && loserTeam) {
              if (userTeam.id === winnerTeam.id) {
                setCelebration({ type: 'champions', winnerTeam });
              } else if (userTeam.id === loserTeam.id) {
                setCelebration({ type: 'secondPlace', loserTeam });
              }
            }
          }
        } else if (userTeamScore?.team_result === 'WINNER') {
          const leadingTeamIds = updatedTournamentsScores
            .filter(s => s.round_number === currentRound && s.team_result === 'WINNER')
            .map(s => s.team_id);
          const leadingTeams = tournamentsTeams.filter(team => leadingTeamIds.includes(team.id));
          const roundName = roundNames[roundNames.length - roundCount + (currentRound - 1)] || '';
          if (leadingTeams.length) {
            setCelebration({ type: 'leader', leadingTeams, roundName });
          }
        }
      }
    } else {
      setAlertType('error');
      setAlertTitle('Error');
      setAlertText('Scores have not been processed yet.');
      setIsAlertVisible(true);
    }

    setIsUpdateScoresButtonLoading(false);
  }

  const handleNextRoundButtonClick = async () => {
    const currentRoundTeamScores = tournamentsTeamScores.filter(score => score.round_number === currentRound);

    if(!currentRoundTeamScores.length) {
      setIsAlertVisible(true);
      setAlertType('error');
      setAlertTitle('Error');
      setAlertText('Instructor hasn\'t processed scores yet.');
      return;
    }

    const { data: freshTournamentsRounds } = await refetchTournamentsRounds();
    const ongoingRound = freshTournamentsRounds.find(round => round.number === currentRound);
    const roundCount = tournament?.round_count || freshTournamentsRounds.length;
    const isChampionshipRound = currentRound === roundCount;

    if (isChampionshipRound) {
      if (!ongoingRound?.is_finished) {
        setIsAlertVisible(true);
        setAlertType('error');
        setAlertTitle('Error');
        setAlertText('The instructor hasn\u2019t finalized the championship yet.');
        return;
      }
      const championshipMatchup = tournamentsMatchups?.find(m => m.round_id === ongoingRound.id);
      if (!championshipMatchup) return;
      const matchupScores = currentRoundTeamScores.filter(s => s.matchup_id === championshipMatchup.id);
      const winnerScoreRow = matchupScores.find(s => s.team_result === 'WINNER');
      const loserScoreRow  = matchupScores.find(s => s.team_result === 'LOSER');
      const winnerTeam = winnerScoreRow ? tournamentsTeams.find(t => t.id === winnerScoreRow.team_id) : null;
      const loserTeam  = loserScoreRow  ? tournamentsTeams.find(t => t.id === loserScoreRow.team_id)  : null;
      if (winnerTeam && loserTeam) {
        const userTeam = tournamentsTeams?.find(team => team.user_ids.includes(user.id));
        if (userTeam?.id === winnerTeam.id) {
          setCelebration({ type: 'champions', winnerTeam });
        } else if (userTeam?.id === loserTeam.id) {
          setCelebration({ type: 'secondPlace', loserTeam });
        }
      }
      return;
    }

    if (!ongoingRound?.is_finished) {
      setIsAlertVisible(true);
      setAlertType('error');
      setAlertTitle('Error');
      setAlertText('The instructor hasn\u2019t advanced to the next round yet.');
    }
  }

  const handleConfirmAlertButtonClick = () => {
    setIsAlertVisible(false);
  }

  const currentRoundData = tournamentsRounds?.[currentRound - 1];
  const isRoundActive = !!currentRoundData?.is_active;
  const middleHeaderButtonActiveClass = isRoundActive ? 'active' : 'disabled';
  const middleHeaderButtonTitle = isRoundActive
    ? ''
    : 'The round is locked. Check with your instructor for further guidance.';

  return (
    <Layout
      isHeaderVisible={true}
      isContentVertical={true}
      backgroundColor="#FFF8EB"
      className="instructor-review-session-layout"
      middleHeaderButtonText="Start"
      middleHeaderButtonClassName={`button-submit review-session-start ${middleHeaderButtonActiveClass}`}
      middleHeaderButtonDataTourId="start-button"
      middleButtonTitle={middleHeaderButtonTitle}
      onMiddleHeaderButtonClick={handleStartButtonClick}
      isMiddleButtonLoading={isStartButtonLoading}
    >
      {
        !tournaments || !tournamentsRounds || !tournamentsMatchups || !tournamentsTeams
        ? <span className="loader"></span>
        : <>
            <ReviewSessionHeader 
              roundCount={tournament?.round_count}
              currentRound={currentRound}
              rounds={tournamentsRounds}
              teams={tournamentsTeams?.filter(team => team.matchups.some(m => m.round_number === currentRound))}
              onUpdateButtonClick={handleUpdateScoresButtonClick}
              isUpdateScoresButtonLoading={isUpdateScoresButtonLoading}
              onNextRoundButtonClick={handleNextRoundButtonClick}
              userScores={userScores}
            />
            {
              isAlertVisible &&
                <Alert 
                  type={alertType}
                  title={alertTitle}
                  text={alertText}
                  confirmButtonText="Ok"
                  onConfirmButtonClick={handleConfirmAlertButtonClick}
                />
            }
            <ReviewSessionTournamentBracket
              roundCount={tournament?.round_count}
              currentRound={currentRound}
              gameType={tournamentRoundGames[currentRound - 1]?.gameType}
              rounds={tournamentsRounds}
              matchups={tournamentsMatchups}
              teams={tournamentsTeams}
              teamScores={tournamentsTeamScores}
              isScoresUpdated={isScoresUpdated}
            />
            {celebration?.type === 'leader' && (
              <CelebrationModal
                variant="variant-leader"
                onClose={() => setCelebration(null)}
                content={
                  <LeaderCelebration
                    leadingTeams={celebration.leadingTeams}
                    roundName={celebration.roundName}
                  />
                }
              />
            )}
            {celebration?.type === 'winnersLosers' && (
              <CelebrationModal
                variant="variant-winners-losers"
                onClose={() => setCelebration(null)}
                content={
                  <WinnersLosers
                    winners={celebration.winnerTeams}
                    losers={celebration.loserTeams}
                    roundName={celebration.roundName}
                  />
                }
              />
            )}
            {celebration?.type === 'champions' && (
              <CelebrationModal
                variant="variant-champions"
                onClose={() => setCelebration(null)}
                content={<Champions team={celebration.winnerTeam} />}
              />
            )}
            {celebration?.type === 'secondPlace' && (
              <CelebrationModal
                variant="variant-second-place"
                onClose={() => setCelebration(null)}
                content={<SecondPlace team={celebration.loserTeam} />}
              />
            )}
          </>
      }
    </Layout>
  );
}
